/**
 * Typed API client.
 *
 * Errors from the backend are always the same shape, so the UI can show the
 * server's own explanation (including why a state transition was refused)
 * instead of inventing a generic message.
 */

import type { DomainErrorBody } from './types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const WEBSOCKET_BASE = import.meta.env.VITE_WEBSOCKET_BASE_URL;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: DomainErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown_error';
    this.details = body?.error?.details ?? {};
  }
}

type TokenBundle = { accessToken: string; refreshToken: string };

let tokens: TokenBundle | null = null;
let onUnauthenticated: (() => void) | null = null;
let refreshInFlight: Promise<void> | null = null;

export function setTokens(next: TokenBundle | null): void {
  tokens = next;
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function refreshTokens(): Promise<void> {
  if (!tokens?.refreshToken) throw new ApiError(401, null, 'Not signed in');
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: tokens?.refreshToken }),
      });
      if (!response.ok) {
        tokens = null;
        onUnauthenticated?.();
        throw new ApiError(response.status, null, 'Session expired');
      }
      const body = (await response.json()) as { access_token: string; refresh_token: string };
      tokens = { accessToken: body.access_token, refreshToken: body.refresh_token };
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function request<T>(
  path: string,
  init: RequestInit & { retryOnUnauthorised?: boolean } = {},
): Promise<T> {
  const { retryOnUnauthorised = true, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (rest.body && !(rest.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (tokens?.accessToken) headers.set('Authorization', `Bearer ${tokens.accessToken}`);

  const response = await fetch(`${BASE}${path}`, { ...rest, headers });

  if (response.status === 401 && retryOnUnauthorised && tokens?.refreshToken) {
    await refreshTokens();
    return request<T>(path, { ...init, retryOnUnauthorised: false });
  }

  const body = await parseBody(response);
  if (!response.ok) {
    const fallback = response.status === 401
      ? 'Email or password is incorrect'
      : response.status === 403
        ? 'You are not allowed to sign in here'
        : response.status >= 500
          ? 'Konnextions is having trouble. Try again in a moment.'
          : response.statusText || 'Request failed';
    throw new ApiError(response.status, body as DomainErrorBody | null, fallback);
  }
  return body as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'POST', body: payload == null ? undefined : JSON.stringify(payload) }),
  patch: <T,>(path: string, payload: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(payload) }),
  put: <T,>(path: string, payload: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(payload) }),
  postForm: <T,>(path: string, payload: FormData) =>
    request<T>(path, { method: 'POST', body: payload }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Fetch a protected binary response (audio/PDF/etc.) using the active session. */
export async function getBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  if (tokens?.accessToken) headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  const response = await fetch(`${BASE}${path}`, { headers });
  if (!response.ok) {
    const body = await parseBody(response);
    throw new ApiError(response.status, body as DomainErrorBody | null, response.statusText || 'Request failed');
  }
  return response.blob();
}

export async function download(path: string, fallbackName: string): Promise<void> {
  const headers = new Headers();
  if (tokens?.accessToken) headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  const response = await fetch(`${BASE}${path}`, { headers });
  if (!response.ok) {
    const body = await parseBody(response);
    throw new ApiError(response.status, body as DomainErrorBody | null, response.statusText);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function login(
  email: string,
  password: string,
  mfaCode?: string,
): Promise<import('./types').LoginResponse> {
  const response = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, mfa_code: mfaCode || null }),
  });
  const body = await parseBody(response);
  if (!response.ok) {
    const fallback = response.status === 401
      ? 'Email or password is incorrect'
      : response.status >= 500
        ? 'Konnextions is having trouble. Try again in a moment.'
        : 'Sign-in failed';
    throw new ApiError(response.status, body as DomainErrorBody | null, fallback);
  }
  const parsed = body as import('./types').LoginResponse;
  setTokens({ accessToken: parsed.access_token, refreshToken: parsed.refresh_token });
  return parsed;
}

export async function demoLogin(
  role: string,
): Promise<import('./types').LoginResponse> {
  const response = await fetch(`${BASE}/demo/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const body = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, body as DomainErrorBody | null, 'Demo login failed');
  }
  const parsed = body as import('./types').LoginResponse;
  setTokens({ accessToken: parsed.access_token, refreshToken: parsed.refresh_token });
  return parsed;
}

export function websocketUrl(topics: string[]): string {
  const token = getAccessToken();
  if (WEBSOCKET_BASE) {
    return `${WEBSOCKET_BASE.replace(/\/$/, '')}/ws?token=${encodeURIComponent(token ?? '')}&topics=${topics.join(',')}`;
  }
  const base = BASE.replace(/\/api\/v1$/, '');
  const origin = base.startsWith('http')
    ? base.replace(/^http/, 'ws')
    : `${window.location.origin.replace(/^http/, 'ws')}${base}`;
  return `${origin}/ws?token=${encodeURIComponent(token ?? '')}&topics=${topics.join(',')}`;
}
